/**
 * FY26 Revenue Waterfall Calculator
 * Based on the FY26 Revenue Waterfall Projection methodology
 */

/**
 * Calculates the complete waterfall forecast based on input metrics
 * @param {object} metrics - Input metrics for calculation
 * @returns {array} Array of monthly waterfall data
 */
export function calculateWaterfall(metrics) {
  const {
    startingArr = 10000000,          // $10M starting ARR (Dec 2025)
    arpa = 194000,                   // $194K average revenue per account
    sqlToSqoRate = 0.434,            // 43.4% SQL → SQO conversion
    sqoToCwRate = 0.288,             // 28.8% SQO → Closed Won conversion
    pipelineCoverageRatio = 3.5,     // 3.5x pipeline coverage
    startingSqls = 52,               // Starting SQLs in Nov 2025
    monthlyGrowthRate = 0.10,        // ~10% monthly growth (default)
    decemberSurge = 25               // December year-end surge deals
  } = metrics;

  // Base case data - 2026 projection only (Jan - Dec 2026)
  // Starting point: Dec 2025 ARR $10,522,000 (not shown in data, used for cumulative calculation)
  // Q1 Total (Jan-Mar): $4.27M | Q2 Total (Apr-Jun): $5.82M | H1 Total: $10.09M
  // ARPA: $194K | Conversion rates: SQL→SQO 43.4%, SQO→CW 28.8%
  const baselineData = [
    { month: 'Jan 2026', sqls: 55, sqos: 24, closedWon: 7, monthlyArr: 1358000, cumulativeArr: 11880000 },
    { month: 'Feb 2026', sqls: 55, sqos: 24, closedWon: 7, monthlyArr: 1358000, cumulativeArr: 13238000 },
    { month: 'Mar 2026', sqls: 65, sqos: 28, closedWon: 8, monthlyArr: 1552000, cumulativeArr: 14790000 },
    { month: 'Apr 2026', sqls: 81, sqos: 35, closedWon: 10, monthlyArr: 1940000, cumulativeArr: 16730000 },
    { month: 'May 2026', sqls: 81, sqos: 35, closedWon: 10, monthlyArr: 1940000, cumulativeArr: 18670000 },
    { month: 'Jun 2026', sqls: 81, sqos: 35, closedWon: 10, monthlyArr: 1940000, cumulativeArr: 20610000 },
    { month: 'Jul 2026', sqls: 97, sqos: 42, closedWon: 12, monthlyArr: 2328000, cumulativeArr: 22938000 },
    { month: 'Aug 2026', sqls: 106, sqos: 46, closedWon: 13, monthlyArr: 2522000, cumulativeArr: 25460000 },
    { month: 'Sep 2026', sqls: 113, sqos: 49, closedWon: 14, monthlyArr: 2716000, cumulativeArr: 28176000 },
    { month: 'Oct 2026', sqls: 123, sqos: 53, closedWon: 15, monthlyArr: 2910000, cumulativeArr: 31086000 },
    { month: 'Nov 2026', sqls: 130, sqos: 56, closedWon: 16, monthlyArr: 3104000, cumulativeArr: 34190000 },
    { month: 'Dec 2026', sqls: 210, sqos: 91, closedWon: 26, monthlyArr: 5044000, cumulativeArr: 39234000 }
  ];

  // Calculate slider adjustments from baseline
  const baselineArpa = 194000; // $194K baseline
  const baselineSqlToSqoRate = 0.434;
  const baselineSqoToCwRate = 0.288;
  const baselineStartingSqls = 55; // Jan 2026 starting SQLs (baseline - updated for Q1 $4.2M target)
  const baselineDecemberSurge = 25;
  const baselineDecember2025Arr = 10522000; // Dec 2025 ARR (starting point for 2026 cumulative)
  const baselineMonthlyGrowthRate = 0.10;
  const baselinePipelineCoverageRatio = 3.5;

  const arpaMultiplier = arpa / baselineArpa;
  const sqlMultiplier = startingSqls / baselineStartingSqls;
  const growthMultiplier = monthlyGrowthRate / baselineMonthlyGrowthRate;
  const startingArrMultiplier = startingArr / baselineDecember2025Arr; // Scale from Dec 2025 baseline
  const surgeAdjustment = decemberSurge - baselineDecemberSurge;

  const monthData = [];
  let cumulativeArr = 0;

  baselineData.forEach((baseData, index) => {
    // Apply SQL scaling sliders (Starting SQLs + Monthly Growth Rate)
    const adjustedSqls = Math.round(baseData.sqls * sqlMultiplier * growthMultiplier);

    // Apply SQL→SQO conversion rate slider
    const adjustedSqos = Math.round(adjustedSqls * sqlToSqoRate);

    // Apply SQO→CW conversion rate slider
    let adjustedClosedWon = Math.round(adjustedSqos * sqoToCwRate);

    // Add December surge adjustment
    if (baseData.month === 'Dec 2026') {
      adjustedClosedWon += surgeAdjustment;
    }

    // Apply ARPA adjustment to monthly ARR
    const adjustedMonthlyArr = Math.round(adjustedClosedWon * arpa);

    // Accumulate ARR with adjustments
    if (index === 0) {
      // For Jan 2026, start with Dec 2025 baseline ARR and add monthly ARR
      cumulativeArr = Math.round(baselineDecember2025Arr * startingArrMultiplier + adjustedMonthlyArr);
    } else {
      cumulativeArr += adjustedMonthlyArr;
    }

    // Calculate pipeline based on adjusted deals and ARPA with Pipeline Coverage Ratio slider
    const pipeline = (adjustedClosedWon * arpa * pipelineCoverageRatio) / 1000000; // Convert to millions

    monthData.push({
      month: baseData.month,
      monthCode: convertToMonthCode(baseData.month),
      sqls: adjustedSqls,
      sqos: adjustedSqos,
      closedWon: adjustedClosedWon,
      monthlyArr: adjustedMonthlyArr,
      cumulativeArr: Math.round(cumulativeArr),
      pipeline: parseFloat(pipeline.toFixed(1)),
      isTotal: false
    });
  });

  // Add H1 Total (Jan 2026 - Jun 2026, indices 0-5)
  const h1Data = monthData.slice(0, 6);
  const h1Total = {
    month: 'H1 Total',
    monthCode: '2026-H1',
    sqls: h1Data.reduce((sum, m) => sum + m.sqls, 0),
    sqos: h1Data.reduce((sum, m) => sum + m.sqos, 0),
    closedWon: h1Data.reduce((sum, m) => sum + m.closedWon, 0),
    monthlyArr: h1Data.reduce((sum, m) => sum + m.monthlyArr, 0),
    cumulativeArr: h1Data[h1Data.length - 1].cumulativeArr,
    pipeline: parseFloat(h1Data.reduce((sum, m) => sum + m.pipeline, 0).toFixed(1)),
    isTotal: true
  };

  // Add H2 Total (Jul 2026 - Dec 2026, indices 6-11)
  const h2Data = monthData.slice(6, 12);
  const h2Total = {
    month: 'H2 Total',
    monthCode: '2026-H2',
    sqls: h2Data.reduce((sum, m) => sum + m.sqls, 0),
    sqos: h2Data.reduce((sum, m) => sum + m.sqos, 0),
    closedWon: h2Data.reduce((sum, m) => sum + m.closedWon, 0),
    monthlyArr: h2Data.reduce((sum, m) => sum + m.monthlyArr, 0),
    cumulativeArr: 0,  // H2 Total shows blank for cumulative ARR
    pipeline: 0,       // H2 Total shows blank for pipeline
    isTotal: true
  };

  // Add 2026 Total (Jan 2026 - Dec 2026, all 12 months)
  const yearTotal = {
    month: '2026 Total',
    monthCode: '2026',
    sqls: monthData.reduce((sum, m) => sum + m.sqls, 0),
    sqos: monthData.reduce((sum, m) => sum + m.sqos, 0),
    closedWon: monthData.reduce((sum, m) => sum + m.closedWon, 0),
    monthlyArr: monthData.reduce((sum, m) => sum + m.monthlyArr, 0),
    cumulativeArr: monthData[monthData.length - 1].cumulativeArr, // Use Dec 2026 cumulative
    pipeline: parseFloat(monthData.reduce((sum, m) => sum + m.pipeline, 0).toFixed(1)),
    isTotal: true
  };

  // Build final data with totals inserted at correct positions
  const finalData = [
    ...monthData.slice(0, 6),  // Jan 2026 - Jun 2026
    h1Total,
    ...monthData.slice(6),     // Jul 2026 - Dec 2026
    h2Total,
    yearTotal
  ];

  return finalData;
}

/**
 * Converts month name to month code (e.g., "Nov 2025" → "2025-11")
 */
function convertToMonthCode(monthStr) {
  const monthNameToNum = {
    'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04', 'May': '05', 'Jun': '06',
    'Jul': '07', 'Aug': '08', 'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12'
  };

  const parts = monthStr.split(' ');
  if (parts.length === 2) {
    const [name, year] = parts;
    return `${year}-${monthNameToNum[name]}`;
  }
  return monthStr;
}

/**
 * Calculates growth rate needed to reach target SQLs
 * @param {number} startingSqls - Starting SQL count
 * @param {number} targetSqls - Target SQL count
 * @param {number} months - Number of months
 * @returns {number} Monthly growth rate
 */
export function calculateRequiredGrowthRate(startingSqls, targetSqls, months) {
  // Formula: targetSqls = startingSqls * (1 + rate)^months
  // Solving for rate: rate = (targetSqls / startingSqls)^(1/months) - 1
  return Math.pow(targetSqls / startingSqls, 1 / months) - 1;
}

/**
 * Generates scenario variations (worst, base, best)
 * @param {object} baseMetrics - Base case metrics
 * @param {number} spreadMultiplier - Scenario spread multiplier (default 1.0)
 * @returns {object} Object with worst, base, and best scenarios
 */
export function generateScenarios(baseMetrics, spreadMultiplier = 1.0) {
  // Base scenario uses the provided metrics as-is
  const baseScenario = calculateWaterfall(baseMetrics);

  // Worst scenario: reduce conversion rates and deal value
  const worstMetrics = {
    ...baseMetrics,
    sqlToSqoRate: baseMetrics.sqlToSqoRate * (1 - 0.10 * spreadMultiplier),
    sqoToCwRate: baseMetrics.sqoToCwRate * (1 - 0.10 * spreadMultiplier),
    arpa: baseMetrics.arpa * (1 - 0.15 * spreadMultiplier),
    monthlyGrowthRate: baseMetrics.monthlyGrowthRate * (1 - 0.25 * spreadMultiplier)
  };
  const worstScenario = calculateWaterfall(worstMetrics);

  // Best scenario: increase conversion rates and deal value
  const bestMetrics = {
    ...baseMetrics,
    sqlToSqoRate: Math.min(baseMetrics.sqlToSqoRate * (1 + 0.15 * spreadMultiplier), 0.65),
    sqoToCwRate: Math.min(baseMetrics.sqoToCwRate * (1 + 0.15 * spreadMultiplier), 0.45),
    arpa: baseMetrics.arpa * (1 + 0.15 * spreadMultiplier),
    monthlyGrowthRate: baseMetrics.monthlyGrowthRate * (1 + 0.30 * spreadMultiplier)
  };
  const bestScenario = calculateWaterfall(bestMetrics);

  return {
    worst: worstScenario,
    base: baseScenario,
    best: bestScenario
  };
}

/**
 * Converts waterfall data to forecast format for Dashboard
 * @param {array} waterfallData - Waterfall data
 * @returns {array} Forecast data format
 */
export function waterfallToForecast(waterfallData) {
  return waterfallData
    .filter(row => !row.isTotal)
    .map(row => ({
      month: row.monthCode,
      sqls: row.sqls,
      sqos: row.sqos,
      closedWon: row.closedWon,
      revenue: row.monthlyArr,
      cumulativeRevenue: row.cumulativeArr,
      pipeline: row.pipeline,
      isHistorical: false
    }));
}
